import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useTheme
} from '@material-ui/core'
import { AddCircleOutline, PersonOutline } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import moment from 'moment'
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { toast } from 'react-toastify'

import { ChevronDown, CircleX } from 'lucide-react'
import { AuthContext } from '../../context/Auth/AuthContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { ClientSelect } from '../ClientSelect'
import { InputFieldText } from '../InputFieldText'
import SelectInput from '../SelectInput'
import { useStyles } from './style'

const ReminderModal = ({ open, onClose, reminderId, onSave }) => {
  const classes = useStyles()
  const theme = useTheme()

  const { user } = useContext(AuthContext)
  const notesInputRef = useRef(null)

  const [reminder, setReminder] = useState({
    empresa: '',
    whatsappId: '',
    clienteId: null,
    cliente: null,
    contactIds: [],
    userId: user.id,
    documentType: '',
    status: 'pending',
    repeat: 'none',
    dueDate: '',
    relativeReminders: [],
    notes: ''
  })

  const [newRelativeDays, setNewRelativeDays] = useState('')

  const [contacts, setContacts] = useState([])
  const [contactLoading, setContactLoading] = useState(false)
  const [whatsapps, setWhatsapps] = useState([])
  const [users, setUsers] = useState([])
  const [savedMessages, setSavedMessages] = useState([])
  const [customFieldNames, setCustomFieldNames] = useState([])

  const baseAvailableVariables = [
    { label: '{{name}}', title: 'Nome completo do contato', icon: true },
    { label: '{{firstname}}', title: 'Primeiro nome do contato', icon: true },
    { label: '{{email}}', title: 'Email do contato' },
    { label: '{{greeting}}', title: 'Saudação (Olá, Bom dia, etc)' },
    { label: '{{time}}', title: 'Hora atual' },
    { label: '{{site}}', title: 'Site do contato' }
  ]

  const customFieldVariables = customFieldNames.map(name => ({
    label: `{{${name}}}`,
    title: `Campo customizado: ${name}`
  }))

  const availableVariables = [
    ...baseAvailableVariables,
    ...customFieldVariables
  ].filter(
    (variable, index, array) =>
      array.findIndex(item => item.label === variable.label) === index
  )

  const documentTypes = ['Simples Nacional', 'Outros']

  const statusOptions = [
    { value: 'pending', label: 'Pendente' },
    { value: 'closed', label: 'Concluído' }
  ]

  const repeatOptions = [
    { value: 'none', label: 'Sem repetição' },
    { value: 'monthly', label: 'Mensal' },
    { value: 'yearly', label: 'Anual' }
  ]

  const fetchReminder = useCallback(async () => {
    try {
      const { data } = await api.get(`/reminders/${reminderId}`)

      // Obter o clienteId através do contato ou diretamente
      let clienteId = data.clienteId
      let cliente = null

      if (
        !clienteId &&
        data.contact &&
        data.contact.clienteContacts &&
        data.contact.clienteContacts.length > 0
      ) {
        clienteId = data.contact.clienteContacts[0].clienteId
      }

      // Buscar o cliente completo pela API se tivermos o clienteId
      if (clienteId) {
        try {
          const { data: clienteData } = await api.get(`/clientes/${clienteId}`)
          cliente = clienteData
        } catch (err) {
          console.error('Erro ao buscar cliente:', err)
        }
      }

      // Converter contactId único para array se necessário
      let contactIds = []
      if (data.contactId) {
        contactIds = [data.contactId]
      }

      setReminder({
        empresa: cliente?.nome || data.empresa || '',
        whatsappId: data.whatsappId || '',
        clienteId: clienteId,
        cliente: cliente,
        contactIds: contactIds,
        userId: data.userId,
        documentType: data.documentType,
        status: data.status,
        repeat: data.repeat || 'none',
        dueDate: data.dueDate
          ? typeof data.dueDate === 'string' &&
            /^\d{4}-\d{2}-\d{2}$/.test(data.dueDate)
            ? data.dueDate
            : moment(data.dueDate).local().format('YYYY-MM-DD')
          : '',
        relativeReminders: data.relativeReminders || [],
        notes: data.notes || ''
      })
    } catch (err) {
      toastError(err)
    }
  }, [reminderId])

  const fetchClienteContacts = useCallback(async () => {
    if (!reminder.clienteId) {
      setContacts([])
      return
    }

    setContactLoading(true)
    try {
      const { data } = await api.get(`/clientes/${reminder.clienteId}/contacts`)
      // A resposta é um array de ClienteContact com contact included
      const contactsList = data.map(cc => cc.contact)
      setContacts(contactsList)
    } catch (err) {
      toastError(err)
    }
    setContactLoading(false)
  }, [reminder.clienteId])

  useEffect(() => {
    if (open) {
      fetchWhatsapps()
      fetchUsers()
      fetchSavedMessages()
      fetchCustomFieldNames()
      if (reminderId) {
        fetchReminder()
      } else {
        setReminder({
          empresa: '',
          whatsappId: '',
          clienteId: null,
          cliente: null,
          contactIds: [],
          userId: user.id,
          documentType: '',
          status: 'pending',
          repeat: 'none',
          dueDate: '',
          relativeReminders: [],
          notes: ''
        })
        setNewRelativeDays('')
      }
    }
  }, [open, reminderId, user.id, fetchReminder])

  useEffect(() => {
    if (reminder.clienteId) {
      fetchClienteContacts()
    } else {
      setContacts([])
    }
  }, [reminder.clienteId, fetchClienteContacts])

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users', {
        params: { profile: 'admin' }
      })
      setUsers(data.users)
    } catch (err) {
      toastError(err)
    }
  }

  const fetchWhatsapps = async () => {
    try {
      const { data } = await api.get('/whatsapp', {
        params: { session: 0 }
      })

      const whatsappConnections = data.filter(w => {
        const channel = (w.channel || '').toLowerCase()
        return channel === 'whatsapp'
      })

      setWhatsapps(whatsappConnections)
    } catch (err) {
      toastError(err)
    }
  }

  const fetchSavedMessages = async () => {
    try {
      const { data } = await api.get('/reminder-messages')
      setSavedMessages(data)
    } catch (err) {
      toastError(err)
    }
  }

  const fetchCustomFieldNames = async () => {
    try {
      const { data } = await api.get('/contacts/custom-fields/names')
      setCustomFieldNames(Array.isArray(data) ? data : [])
    } catch (err) {
      toastError(err)
      setCustomFieldNames([])
    }
  }

  const handleSave = async () => {
    try {
      if (
        !reminder.empresa ||
        !reminder.documentType ||
        !reminder.userId ||
        !reminder.dueDate
      ) {
        toast.error('Preencha todos os campos obrigatórios')
        return
      }

      // Preparar dados para envio sem o objeto cliente completo
      const dataToSend = {
        whatsappId: reminder.whatsappId || null,
        clienteId: reminder.clienteId,
        contactIds: reminder.contactIds,
        userId: reminder.userId,
        documentType: reminder.documentType,
        status: reminder.status,
        repeat: reminder.repeat,
        dueDate: reminder.dueDate,
        relativeReminders: reminder.relativeReminders || [],
        notes: reminder.notes,
        empresa: reminder.empresa
      }

      if (reminderId) {
        await api.put(`/reminders/${reminderId}`, dataToSend)
        toast.success('Lembrete atualizado com sucesso!')
      } else {
        await api.post('/reminders', dataToSend)
        if (reminder.contactIds && reminder.contactIds.length > 1) {
          toast.success(
            `${reminder.contactIds.length} lembretes criados com sucesso!`
          )
        } else {
          toast.success('Lembrete criado com sucesso!')
        }
      }

      onSave()
    } catch (err) {
      toastError(err)
    }
  }

  const handleChange = (field, value) => {
    setReminder(prev => ({ ...prev, [field]: value }))
  }

  const insertVariable = variable => {
    const input = notesInputRef.current
    if (!input) return

    const textarea = input.querySelector('textarea')
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const currentText = reminder.notes || ''
    const newText =
      currentText.substring(0, start) + variable + currentText.substring(end)

    handleChange('notes', newText)

    // Reposiciona o cursor após a variável inserida
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(
        start + variable.length,
        start + variable.length
      )
    }, 0)
  }

  const handleAddRelativeReminder = () => {
    const days = parseInt(newRelativeDays, 10)
    if (isNaN(days)) return
    const existing = reminder.relativeReminders || []
    if (existing.some(r => r.days === days)) return
    handleChange('relativeReminders', [...existing, { days }])
    setNewRelativeDays('')
  }

  const handleRemoveRelativeReminder = days => {
    const existing = reminder.relativeReminders || []
    handleChange(
      'relativeReminders',
      existing.filter(r => r.days !== days)
    )
  }

  const getChipClass = days => {
    if (days < 0) return classes.chipBefore
    if (days > 0) return classes.chipAfter
    return classes.chipExact
  }

  const getChipLabel = days => {
    if (days === 0) return 'Vencimento'

    if (days === -1) return '1 dia antes'
    if (days < 0) return `${Math.abs(days)} dias antes`

    if (days === 1) return '1 dia depois'
    return `${days} dias depois`
  }

  const hasReminders =
    reminder.relativeReminders && reminder.relativeReminders.length > 0

  const renderContacts = () => {
    const ALL_CONTACTS_OPTION = { id: 'all', name: 'Todos os contatos' }

    const allContactsOptions = [ALL_CONTACTS_OPTION, ...contacts]

    const isAllSelected =
      contacts.length > 0 &&
      reminder.contactIds &&
      reminder.contactIds.length === contacts.length
    return (
      <FormControl className={classes.formControl}>
        <FormLabel>Contatos (Opcional)</FormLabel>
        <Autocomplete
          multiple
          size="small"
          options={allContactsOptions}
          loading={contactLoading}
          disabled={!reminder.clienteId}
          disableCloseOnSelect
          getOptionLabel={option => option.name || option.number || ''}
          value={contacts.filter(c => reminder.contactIds.includes(c.id))}
          onChange={(e, newValues) => {
            const isSelectAllClicked = newValues.some(
              option => option.id === 'all'
            )

            let nextContactIds = []

            if (isSelectAllClicked) {
              nextContactIds = isAllSelected ? [] : contacts.map(c => c.id)
            } else {
              nextContactIds = newValues.map(v => v.id)
            }

            handleChange('contactIds', nextContactIds)
          }}
          renderOption={(option, { selected }) => {
            const isChecked = option.id === 'all' ? isAllSelected : selected

            return (
              <>
                <Checkbox
                  color="primary"
                  style={{ marginRight: 8, padding: 0 }}
                  checked={isChecked}
                />
                <span className={classes.optionText}>
                  {option.name || option.number}
                </span>
              </>
            )
          }}
          renderTags={(value, getTagProps) => {
            const renderedTags = value.slice(0, 1).map((option, index) => (
              <Chip
                key={option.id}
                variant="outlined"
                className={classes.chip}
                style={{
                  borderRadius: '4px',
                  height: '20px',
                  fontSize: '11px',
                  color:
                    theme.palette.type === 'dark'
                      ? theme.palette.dark_50?.main
                      : theme.palette.gray_400?.main
                }}
                label={option.name || option.number}
                {...getTagProps({ index })}
                size="small"
                deleteIcon={<CircleX />}
              />
            ))

            if (value.length > 1) {
              renderedTags.push(
                <Chip
                  key="more"
                  label={`+${value.length - 1}`}
                  size="small"
                  style={{
                    height: '20px',
                    fontSize: '11px',
                    padding: '0 2px',
                    backgroundColor: theme.palette.blue_300?.main,
                    color: theme.palette.white_100?.main
                  }}
                />
              )
            }

            return renderedTags
          }}
          popupIcon={<ChevronDown className={classes.popupIcon} />}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={
                reminder.contactIds && reminder.contactIds.length > 0
                  ? ''
                  : reminder.clienteId
                    ? 'Selecione contatos...'
                    : 'Selecione um cliente primeiro'
              }
              InputProps={{
                ...params.InputProps,
                style: { padding: '8px 4px' }
              }}
            />
          )}
        />
      </FormControl>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        {reminderId ? 'Editar Lembrete' : 'Novo Lembrete'}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12}>
            <SelectInput
              value={reminder.whatsappId}
              onChange={e => handleChange('whatsappId', e.target.value)}
              label="Conexão para Envio"
            >
              <MenuItem value="">Conexão padrão da empresa</MenuItem>
              {whatsapps.map(whatsapp => (
                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                  {whatsapp.name || `Conexão #${whatsapp.id}`}
                  {whatsapp.status ? ` (${whatsapp.status})` : ''}
                </MenuItem>
              ))}
            </SelectInput>
          </Grid>

          <Grid item xs={8}>
            <ClientSelect
              label="Cliente *"
              value={reminder.cliente}
              placeholder="Pesquise pelo cliente"
              onSelected={(clienteId, cliente) => {
                handleChange('empresa', cliente?.nome || '')
                handleChange('clienteId', clienteId)
                handleChange('cliente', cliente)
                handleChange('contactIds', [])
              }}
              allowCreate={false}
            />
          </Grid>

          <Grid item xs={4}>
            <SelectInput
              value={reminder.documentType}
              onChange={e => handleChange('documentType', e.target.value)}
              label="Tipo de Documento *"
            >
              {documentTypes.map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </SelectInput>
          </Grid>

          {reminderId && (
            <Grid item xs={4}>
              <SelectInput
                value={reminder.status}
                onChange={e => handleChange('status', e.target.value)}
                label="Status"
              >
                {statusOptions.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </SelectInput>
            </Grid>
          )}

          <Grid item xs={4}>
            <SelectInput
              value={reminder.repeat}
              onChange={e => handleChange('repeat', e.target.value)}
              label="Repetição"
            >
              {repeatOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </SelectInput>
          </Grid>

          <Grid item xs={4}>
            <SelectInput
              value={reminder.userId}
              onChange={e => handleChange('userId', e.target.value)}
              label="Responsável *"
            >
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </SelectInput>
          </Grid>

          <Grid item xs={4}>
            <InputFieldText
              label="Data de Vencimento *"
              type="date"
              variant="outlined"
              fullWidth
              value={reminder.dueDate}
              onChange={e => handleChange('dueDate', e.target.value)}
              InputLabelProps={{
                shrink: true
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              className={`${classes.remindersContainer} ${hasReminders ? classes.remindersMargin : ''}`}
            >
              {(reminder.relativeReminders || [])
                .slice()
                .sort((a, b) => a.days - b.days)
                .map(rel => (
                  <Chip
                    key={rel.days}
                    label={getChipLabel(rel.days)}
                    onDelete={() => handleRemoveRelativeReminder(rel.days)}
                    className={`${classes.chips} ${getChipClass(rel.days)}`} // Usando className no lugar de style
                    size="small"
                  />
                ))}
            </Box>

            <Grid item xs={12} className={classes.inputWrapper}>
              <InputFieldText
                label="Lembrete Relativo (dias antes ou depois do vencimento)"
                name="lembrete"
                id="lembrete"
                type="number"
                value={newRelativeDays}
                onChange={e => setNewRelativeDays(e.target.value)}
                placeholder="Ex: -2 = 2 dias antes, 3 = 3 dias depois, 0 = vencimento"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddRelativeReminder()
                  }
                }}
                helperText="(-) Negativo = dias antes |  (+) Positivo = dias depois | (0) Exato dia do vencimento"
              />

              <IconButton
                className={classes.iconButtonAdd}
                onClick={handleAddRelativeReminder}
                disabled={newRelativeDays === ''}
              >
                <AddCircleOutline />
              </IconButton>
            </Grid>
          </Grid>

          <Grid item xs={6}>
            {renderContacts()}
          </Grid>

          <Grid item xs={6}>
            <SelectInput
              value=""
              onChange={e => handleChange('notes', e.target.value)}
              label="Mensagem Pré-cadastrada"
            >
              <MenuItem value="">Nenhuma</MenuItem>
              {savedMessages.map(msg => (
                <MenuItem key={msg.id} value={msg.message}>
                  {msg.message.substring(0, 50)}
                  {msg.message.length > 50 ? '...' : ''}
                </MenuItem>
              ))}
            </SelectInput>
          </Grid>

          <Grid item xs={12}>
            <Box mb={1}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Variáveis disponíveis:
              </Typography>
              <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                {availableVariables.map(variable => (
                  <Chip
                    key={variable.label}
                    icon={variable.icon ? <PersonOutline /> : undefined}
                    label={variable.label}
                    onClick={() => insertVariable(variable.label)}
                    color="primary"
                    variant="outlined"
                    size="small"
                    style={{ cursor: 'pointer' }}
                    title={variable.title}
                  />
                ))}
              </Box>
            </Box>
            <TextField
              ref={notesInputRef}
              label="Mensagem"
              variant="outlined"
              fullWidth
              multiline
              minRows={4}
              value={reminder.notes}
              onChange={e => handleChange('notes', e.target.value)}
              helperText="Use as variáveis para inserir dados do contato e campos customizados na mensagem"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonSecondary onClick={onClose} label="Cancelar" />
        <ButtonPrimary onClick={handleSave} label="Salvar" />
      </DialogActions>
    </Dialog>
  )
}

export default ReminderModal
